import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import services from '@C/Banking/_utils/data';
import { formatCurrency, TableGrid } from '@C/TableGrid';
import { POSList } from '@C/Banking/_utils/POSList';
import DropLoaderLarge from '@C/Utils/DropLoaderLarge';
import { authHeader } from '@H';
import { DateFilter, DateRangeFilter } from '@C/Utils';

import './styles.scoped.scss';

const POSLedger = () => {
  const [posList, setPosList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [txid, setTxid] = useState([]);
  const [bankOptions, setBankOptions] = useState([]);
  const [posData, setPOSData] = useState({
    offset: 1,
    count: 0,
    limit: 20,
    bank_id: '',
    start_date: dayjs().format('YYYY-MM-DD'),
    end_date: dayjs().format('YYYY-MM-DD'),
    time_stamp: dayjs().format('YYYY-MM-DD'),
    uuid: '',
    refreshList: false,
  });

  const limitInput = useRef();
  const posListRef = useRef();

  const col = [
    {
      name: 'Select',
      key: 'all',
      formatter(props) {
        return (
          <>
            {' '}
            <input value={props.uuid} type="checkbox" onChange={handleChange} />
          </>
        );
      },
    },
    {
      name: 'Date',
      key: 'time_stamp',
      sortable: true,
      type: 'date',
    },
    {
      name: 'Narration',
      key: 'narration',
    },
    {
      name: 'Amount credited',
      key: 'all',
      formatter(props) {
        return <>{formatCurrency(props.amount)}</>;
      },
    },
    {
      name: 'Settlement date',
      key: 'settlement_date',
      type: 'date',
    },
  ];

  useEffect(() => {
    getMappedPOS();
  }, []);

  useEffect(() => {
    if (posData.bank_id) {
      getPOSLedger();
    }
  }, [posData.refreshList]);

  const getPOSLedger = () => {
    setLoading(true);
    services
      .getPOSLedger(posData)
      .then((response) => {
        const tmp = response.data.data.results;
        setPOSData((prevData) => ({
          ...prevData,
          count: response.data.data.count,
        }));
        setPosList(tmp);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  const getMappedPOS = () => {
    services
      .getMappedPOS()
      .then((response) => {
        const tmp = response.data.data;
        const CategoryOptions = tmp.map((item) => ({
          value: item.bunk_card_uuid,
          label: item.name,
        }));
        setBankOptions(CategoryOptions);
        if (CategoryOptions.length > 0) {
          const defaultBankId = CategoryOptions[0].value;
          setPOSData((prevData) => ({
            ...prevData,
            bank_id: defaultBankId,
            refreshList: !prevData.refreshList,
          }));
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handlePagination = (e) => {
    setPOSData((prevData) => ({
      ...prevData,
      offset: e,
      refreshList: !prevData.refreshList,
    }));
  };

  const handleDropChange = (e) => {
    setPOSData((prevData) => ({
      ...prevData,
      bank_id: e?.value,
    }));
  };

  const resetFilter = () => {
    posListRef.current.handleReset();
    setPOSData({
      offset: 1,
      count: 0,
      limit: 20,
      bank_id: bankOptions[0]?.value || '',
      start_date: dayjs().format('YYYY-MM-DD'),
      end_date: dayjs().format('YYYY-MM-DD'),
      time_stamp: dayjs().format('YYYY-MM-DD'),
      refreshList: !posData.refreshList,
    });
    setTxid([]);
  };

  const applyForm = (e) => {
    e.preventDefault();
    setLoading(true);
    setPOSData((prevData) => ({
      ...prevData,
      refreshList: !prevData.refreshList,
    }));
  };

  const handleQuickSearch = (e) => {
    e.preventDefault();
    if (limitInput.current.value === '' || limitInput.current.value === '0') {
      return;
    }
    setPOSData({
      ...posData,
      limit: limitInput.current.value,
      refreshList: !posData.refreshList,
    });
  };

  const handleChange = (e) => {
    if (e.target.checked) {
      setTxid((prevState) => [...prevState, e.target.value]);
    } else {
      const arr = txid.filter((item) => item !== e.target.value);
      setTxid(arr);
    }
  };

  const handleDate = (event) => {
    const value = event.target.value;
    setPOSData((prevData) => ({
      ...prevData,
      time_stamp: value,
    }));
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;

    setPOSData((prevData) => {
      if (name === 'start_date') {
        const newEndDate = dayjs(value).add(7, 'days').format('YYYY-MM-DD');
        return {
          ...prevData,
          start_date: value,
          end_date: newEndDate,
        };
      } else if (name === 'end_date') {
        if (dayjs(value).isBefore(prevData.start_date)) {
          toast.error('To date cannot be before the from date.');
          return prevData;
        }
        return {
          ...prevData,
          end_date: value,
        };
      } else {
        return {
          ...prevData,
          [name]: value,
        };
      }
    });
    setLoading(false);
  };

  const submitForm = () => {
    if (txid.length === 0) {
      toast.error('Please select transactions to mark as settled.');
      return;
    }
    const headers = authHeader(1);
    axios({
      method: 'get',
      url: `${process.env.API_URL}/payment/settlement/?date=${posData.time_stamp}&tx_id=${txid.join(',')}`,
      headers: headers,
    })
      .then((response) => {
        toast.success('Transactions marked as settled successfully');
        setPOSData((prevData) => ({
          ...prevData,
          refreshList: !prevData.refreshList,
        }));
      })
      .catch((error) => {
        toast.error(error.response.data.message);
      });
  };

  return (
    <>
      <section className="widget full-width list-table inverted">
        <header>
          <h3> POS Settlement</h3>
        </header>
        <main>
          <div className="table-filters">
            <div className="filter-wrapper">
              <div className="filter-group">
                <label>Date range:</label>
                <DateRangeFilter
                  value={[posData.start_date, posData.end_date]}
                  onChange={(dates) => {
                    setPOSData((prevProps) => ({
                      ...prevProps,
                      start_date: dates[0],
                      end_date: dates[1],
                    }));
                  }}
                  limit={7}
                  limitType="days"
                />
              </div>
              <div className="filter-group">
                <label>Select POS machine:</label>
                <POSList
                  value={posData.bank_id}
                  handleBankChange={handleDropChange}
                  ref={posListRef}
                />
              </div>
              <div className="filter-group with-icon">
                <label>
                  Results to display- {posData.limit || 20} (Total:{' '}
                  {posData.count})
                </label>
                <form onSubmit={handleQuickSearch}>
                  <input type="text" name="limit" ref={limitInput} />
                  <button
                    type="submit"
                    className="search-icon"
                    onClick={handleQuickSearch}
                  ></button>
                </form>
              </div>
              <div className="filter-group reset-filter">
                <button onClick={resetFilter}>Reset filters</button>
              </div>
              <div className="filter-group">
                <button
                  className={`submit ${loading ? 'loading' : ''}`}
                  type="submit"
                  onClick={applyForm}
                  disabled={loading}
                >
                  Apply filter
                </button>
              </div>
            </div>
          </div>
          <div className="form-header">
            <div className="input-wrapper with-label">
              <label> Select Settlement Date </label>
              <DateFilter
                className="cstm-date-input"
                value={posData.time_stamp}
                name="time_stamp"
                onChange={handleDate}
              />
            </div>

            <div className="form-actions">
              <button className="submit" type="submit" onClick={submitForm}>
                Mark as Settled
              </button>
            </div>
          </div>
          <div className="table-wrapper">
            <div className="mini-summary">
              <ul>
                <li>
                  <span>Total amount credited: </span>
                  <span>
                    {new Intl.NumberFormat('en-IN', {
                      style: 'currency',
                      currency: 'INR',
                    }).format(
                      posList.reduce((a, b) => a + (b['amount'] || 0), 0) || 0,
                    )}
                  </span>
                </li>
              </ul>
            </div>
            {loading ? (
              <DropLoaderLarge />
            ) : (
              <TableGrid
                col={col}
                row={posList}
                offset={posData.offset}
                count={posData.count}
                limit={posData.limit}
                handlePagination={handlePagination}
                hideActions={true}
                enableExport={false}
                selectedList={txid}
              />
            )}
          </div>
        </main>
      </section>
    </>
  );
};

export { POSLedger };
